import React, { useEffect, useState } from "react";
import { Image as ImageIcon, Upload, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const publicImageUrl = (imageId) => `${BACKEND}/api/images/public/${imageId}.jpg`;

const fmtErr = (e, f) => e?.response?.data?.detail || e?.message || f || "Something went wrong.";

// Downscale/compress an image in the browser BEFORE upload, so large phone
// photos don't exceed the proxy body limit (the cause of the Cloudflare 520).
// Returns a small JPEG File; falls back to the original if it can't process it.
const shrinkImage = (file, maxEdge = 1600, quality = 0.85) =>
  new Promise((resolve) => {
    if (!file || !file.type?.startsWith("image/")) return resolve(file);
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        let { width, height } = img;
        if (Math.max(width, height) > maxEdge) {
          const scale = maxEdge / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (!blob) return resolve(file);
            const name = (file.name || "image").replace(/\.(heic|heif|png|webp)$/i, ".jpg");
            resolve(new File([blob], name.endsWith(".jpg") ? name : `${name}.jpg`, { type: "image/jpeg" }));
          },
          "image/jpeg",
          quality,
        );
      } catch {
        URL.revokeObjectURL(url);
        resolve(file);
      }
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

// Shared image picker modal — pick from the library or upload a new image.
const ImagePicker = ({ onClose, onPick }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/images/list");
        setImages(data || []);
      } catch (e) {
        toast.error(fmtErr(e, "Couldn't load your library."));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const prepared = await shrinkImage(file);
      if (!prepared) {
        toast.error("Couldn't read that image. If it's an iPhone HEIC photo, try saving it as JPEG first.");
        return;
      }
      const fd = new FormData();
      fd.append("file", prepared);
      const { data } = await api.post("/images/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Added to your library.");
      onPick(publicImageUrl(data.image_id), file.name || "Uploaded image");
    } catch (e) {
      toast.error(fmtErr(e, "Upload failed."));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" data-testid="shared-image-picker">
      <div className="w-full max-w-2xl bg-[#0A192F] border border-cyan-400/30 rounded-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h3 className="font-heading font-bold text-white text-sm flex items-center gap-2">
            <ImageIcon size={15} className="text-cyan-300" /> Choose an image
          </h3>
          <button onClick={onClose} data-testid="shared-image-picker-close" className="text-slate-400 hover:text-white">
            <X size={18} />
          </button>
        </div>
        <div className="flex gap-2 px-5 pt-4">
          <span className="px-3 py-1.5 rounded-sm text-xs border border-cyan-400/50 bg-cyan-400/10 text-cyan-200">Library</span>
          <label className="px-3 py-1.5 rounded-sm text-xs border border-white/10 text-slate-300 hover:border-cyan-400/40 cursor-pointer inline-flex items-center gap-1">
            {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Upload new
            <input type="file" accept="image/*" className="hidden" data-testid="shared-image-upload-input"
              onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        </div>
        <div className="p-5 max-h-[420px] overflow-auto">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-cyan-300" /></div>
          ) : images.length === 0 ? (
            <p className="text-slate-500 text-sm text-center py-8">Your library is empty — upload an image to get started.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {images.map((img) => (
                <button key={img.image_id}
                  onClick={() => onPick(publicImageUrl(img.image_id), img.prompt?.slice(0, 24) || "Library image")}
                  data-testid={`shared-image-${img.image_id}`}
                  className="aspect-square bg-[#050B14] border border-white/10 hover:border-cyan-400/60 rounded-sm overflow-hidden">
                  {img.data_url && <img src={img.data_url} alt="" className="w-full h-full object-cover" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImagePicker;
